/**
 * Pure query helper functions
 * These only operate on passed data, no component dependencies
 */

// Extract facility data from graphic
export const queryFacilityData = (graphic) => {
    if (!graphic || !graphic.attributes) {
      return { processedVolume: 0 };
    }
    
    const attributes = graphic.attributes;
    const processedVolume = parseFloat(attributes.processedVolume);
    
    return {
      processedVolume: isNaN(processedVolume) ? 0 : processedVolume,
    };
  };
  
  // Get biochar recommendation based on soil analysis
  export const getBiocharRecommendation = (soilAnalysis) => {
    if (!soilAnalysis) {
      return "Insufficient data for recommendation.";
    }
    
    const pH = parseFloat(soilAnalysis.pH?.average);
    const sandTexture = parseFloat(soilAnalysis.sandTexture?.average);
    const soilCarbon = parseFloat(soilAnalysis.soilCarbon?.average);
    
    if (isNaN(pH) || isNaN(sandTexture) || isNaN(soilCarbon)) {
      return "Insufficient data for recommendation.";
    }
  
    let recommendation = "";
  
    if (pH < 5.5) {
      recommendation += "Acidic (<5.5) - Biochar improves nutrient availability. ";
    } else if (pH > 7.4) {
      recommendation += "Alkaline (>7.4) - Biochar impact varies. ";
    } else {
      recommendation += "Neutral (5.5-7.4) - Optimal for maize. ";
    }
  
    if (sandTexture < 60) {
      recommendation += "Low sand (<60%) - Biochar impact on moisture retention is lower. ";
    } else if (sandTexture > 70) {
      recommendation += "High sand (>70%) - Biochar significantly improves moisture. ";
    } else {
      recommendation += "Moderate sand (60-70%) - Biochar enhances water-holding. ";
    }
  
    if (soilCarbon < 12) {
      recommendation += "Low SOC (< 12 g/kg) - Biochar improves sustainability. ";
    } else {
      recommendation += "Moderate SOC (≥ 12 g/kg) - Biochar still beneficial. ";
    }
  
    return recommendation.trim();
  };
  
  // Calculate biochar summary statistics
  export const calculateBiocharSummary = (features, emissionsData) => {
    if (!features || !features.length) {
      return {
        totalBiochar: 0,
        co2Removal: 0,
        applicationCount: 0,
      };
    }
  
    // Filter out experimental batches
    const filteredFeatures = features.filter((feature) => {
      const batchIds = feature.attributes?.batchIds;
      return batchIds && !batchIds.includes("EXPERIMENTAL");
    });
  
    if (filteredFeatures.length === 0) {
      return {
        totalBiochar: 0,
        co2Removal: 0,
        applicationCount: 0,
      };
    }
  
    const totalBiocharAmount = filteredFeatures.reduce((sum, app) => {
      return sum + (app.attributes?.amountOfBiochar || 0);
    }, 0);
  
    const totalC02 = filteredFeatures.reduce((sum, app) => {
      return sum + (app.attributes?.netCo2 || 0);
    }, 0);
  
    let netCO2Removal = totalC02;
    
    if (emissionsData && emissionsData.totalTruckEmissions) {
      const totalDistributorEmissions = emissionsData.totalTruckEmissions / 1000;
      netCO2Removal = totalC02 - totalDistributorEmissions;
    }
  
    const totalBiochar = totalBiocharAmount / 1000;
  
    return {
      totalBiochar: parseFloat(totalBiochar.toFixed(2)),
      co2Removal: parseFloat(netCO2Removal.toFixed(2)),
      applicationCount: filteredFeatures.length,
    };
  };
  
  // Calculate factory utilization metrics
  export const calculateFactoryMetrics = (
    factoryCount,
    maizeProduction,
    oilPalmProduction,
    processedVolume
  ) => {
    const validMaizeProduction = parseFloat(maizeProduction) || 0;
    const validOilPalmProduction = parseFloat(oilPalmProduction) || 0;
    const validProcessedVolume = parseFloat(processedVolume) || 0;
  
    const totalFeedstockAvailable = validMaizeProduction + validOilPalmProduction;
    const factoryInputCapacity = validProcessedVolume * 365;
    const yieldFactor = 0.25;
    const maxBiocharOutput = totalFeedstockAvailable * yieldFactor;
    const utilizedFeedstock = Math.min(totalFeedstockAvailable, factoryInputCapacity);
    
    const factoryUtilization = factoryInputCapacity > 0
      ? (utilizedFeedstock / factoryInputCapacity) * 100
      : 0;
  
    let productionStatus;
    if (totalFeedstockAvailable < factoryInputCapacity) {
      productionStatus = "Under production (insufficient feedstock)";
    } else {
      productionStatus = "At full capacity";
    }
  
    return {
      factoryCount: factoryCount || 0,
      totalFeedstockAvailable,
      maizeProduction: validMaizeProduction,
      pksProduction: validOilPalmProduction,
      factoryInputCapacity,
      maxBiocharOutput,
      factoryUtilization,
      productionStatus,
    };
  };
  
  // Calculate offtake suitability metrics
  export const calculateOfftakeMetrics = (
    commercialFarmCount,
    smallFarmCount,
    totalFarmArea,
    bufferAreaHectares
  ) => {
    const percentAreaUsed = bufferAreaHectares > 0
      ? (totalFarmArea / bufferAreaHectares) * 100
      : 0;
  
    return {
      commercialFarmCount: commercialFarmCount || 0,
      smallFarmCount: smallFarmCount || 0,
      totalFarmArea: totalFarmArea || 0,
      selectedBufferArea: bufferAreaHectares || 0,
      percentAreaUsed: percentAreaUsed.toFixed(2),
    };
  };